// src/components/ProjectDashboardOverview.jsx

import React from 'react';
import ReactECharts from 'echarts-for-react';
import { FiBriefcase, FiCheckCircle, FiClock, FiAlertCircle } from 'react-icons/fi';

// A reusable card component to display a single statistic.
// It accepts a title, a value, an icon, and styling properties.
const StatCard = ({ title, value, icon, bgColor, textColor }) => (
    <div className={`flex flex-col items-start p-4 rounded-lg shadow-sm ${bgColor} text-gray-800`}>
        {/* Icon with background for visual appeal */}
        <div className={`p-2 rounded-full ${bgColor} shadow-md`}>
            {React.cloneElement(icon, { size: 24, className: textColor })}
        </div>
        {/* Title of the statistic */}
        <div className="mt-2 text-sm font-medium text-gray-500">{title}</div>
        {/* The main value of the statistic */}
        <div className="text-3xl font-bold mt-1">{value}</div>
    </div>
);

// The main component that combines the stat cards and the pie chart.
const ProjectDashboardOverview = ({ stats }) => {
    // Data for the pie chart, based on the project statuses.
    // It filters out statuses with a value of 0 to avoid empty slices.
    const pieChartData = [
        { value: stats.planning, name: 'Planning' },
        { value: stats.inProgress, name: 'In Progress' },
        { value: stats.onHold, name: 'On Hold' },
        { value: stats.completed, name: 'Completed' },
        { value: stats.cancelled, name: 'Cancelled' },
        { value: stats.archived, name: 'Archived' },
    ].filter(item => item.value > 0); // Exclude zero values

    // ECharts configuration for the 3D pie chart.
    const pieChartOptions = {
        tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)',
        },
        legend: {
            orient: 'vertical',
            left: 'left',
            data: pieChartData.map(item => item.name),
        },
        series: [
            {
                name: 'Project Status',
                type: 'pie',
                radius: ['40%', '70%'],
                center: ['60%', '50%'],
                roseType: 'radius',
                stillShowZeroSum: false, // Ensures slices with 0 are not shown
                label: {
                    show: false, // Hides the label on the pie chart slices
                    position: 'center',
                },
                emphasis: {
                    label: {
                        show: true, // Shows the label on hover
                        fontSize: '20',
                        fontWeight: 'bold',
                    },
                },
                labelLine: {
                    show: false, // Hides the connecting lines to labels
                },
                data: pieChartData,
            },
        ],
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Container for the stat cards */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-lg font-semibold mb-4">Project Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <StatCard
                        title="Total Projects"
                        value={stats.totalProjects}
                        icon={<FiBriefcase />}
                        bgColor="bg-blue-100"
                        textColor="text-blue-600"
                    />
                    <StatCard
                        title="In Progress"
                        value={stats.inProgress}
                        icon={<FiClock />}
                        bgColor="bg-yellow-100"
                        textColor="text-yellow-600"
                    />
                    <StatCard
                        title="Completed Projects"
                        value={stats.completed}
                        icon={<FiCheckCircle />}
                        bgColor="bg-green-100"
                        textColor="text-green-600"
                    />
                    <StatCard
                        title="Overdue Projects"
                        value={stats.overdue}
                        icon={<FiAlertCircle />}
                        bgColor="bg-red-100"
                        textColor="text-red-600"
                    />
                </div>
            </div>

            {/* Container for the 3D pie chart */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
                <h2 className="text-lg font-semibold mb-4">Projects by Status</h2>
                <ReactECharts option={pieChartOptions} style={{ height: '350px' }} />
            </div>
        </div>
    );
};

export default ProjectDashboardOverview;